/**
 * /well-servicing/activity-codes — the tenant's activity codes
 * (GET / POST / PATCH /well-servicing/activity-codes, fieldiq-api#1269).
 *
 * A tour sheet's time is booked against these codes, and each code's BILLING
 * CLASS says what that time bills as: rig rate, standby, travel, or not
 * billable. The list is the tenant's own: the starter set is a copy, editable.
 *
 * A code is EDITED IN A DIALOG on this register, not on a page of its own:
 * the API has no GET for one code, and a code is four fields. A code is never
 * deleted (a ticket may already carry it); it is RETIRED (active = false) and
 * the Retired chip shows it.
 *
 * "Add starter codes" posts /activity-codes/starter, which inserts the starter
 * codes the tenant does not hold and skips the rest; the result says both
 * counts and names each skipped code, so a second press reads as "nothing
 * new", not as a silent no-op. The header's "Starter missing" is the API's
 * count, so the button only shows when there is something to add.
 *
 * Every write is WS_ADMIN (admin, office_staff). Other roles read the list
 * with no row target. A refusal (409 duplicate_activity_code, 403) reads as a
 * sentence in the dialog, never as a green toast.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ListChecks, PackagePlus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useAddWsStarterCodes,
  useCreateWsActivityCode,
  useUpdateWsActivityCode,
  useWsActivityCodes,
  useWsMeta,
  type WsCodeActive,
} from '../../hooks/useWellServicing';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminWs, normalizeWsCode, wsBillingSkin, wsRefusal, WS_CODE_PATTERN, WS_RETIRED_SKIN } from '../../lib/well-servicing';
import type { WsActivityCode, WsActivityCodeWrite, WsCatalog, WsStarterResult } from '../../types/well-servicing';
import {
  WS_EYEBROW,
  WS_INPUT,
  WS_PRIMARY,
  WS_SECONDARY,
  WsBadge,
  WsChip,
  WsChipRow,
  WsDialog,
  WsEmpty,
  WsField,
  WsFormError,
  WsPanel,
  WsRegister,
  type WsColumn,
} from './WsBits';

const FILTERS: { value: WsCodeActive; label: string }[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Retired' },
  { value: 'all', label: 'All' },
];

/** The first thing wrong with the code form, as a sentence; null when it may be sent. */
export function codeFormError(f: { code: string; label: string; billing_class: string; sort_order: string }): string | null {
  const code = normalizeWsCode(f.code);
  if (!code) return 'Give the code, e.g. 9-KILL.';
  if (code.length > 20) return 'A code is at most 20 characters.';
  if (!WS_CODE_PATTERN.test(code)) return 'A code starts with a letter or digit and holds only letters, digits, "-" and "_".';
  if (!f.label.trim()) return 'Say what the code is for, e.g. Killing the well.';
  if (!f.billing_class) return 'Pick what time on this code bills as.';
  const o = f.sort_order.trim();
  if (o !== '' && (!/^\d+$/.test(o) || Number(o) > 100000)) return 'Order must be a whole number from 0 to 100000.';
  return null;
}

function CodeDialog({ code, catalog, onClose }: { code: WsActivityCode | null; catalog: WsCatalog | null; onClose: () => void }) {
  const create = useCreateWsActivityCode();
  const update = useUpdateWsActivityCode();
  const [form, setForm] = useState({
    code: code?.code ?? '',
    label: code?.label ?? '',
    billing_class: code?.billingClass ?? '',
    sort_order: code ? String(code.sortOrder) : '',
    active: code?.active ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    setError(null);
    const problem = codeFormError(form);
    if (problem) return setError(problem);
    const body: WsActivityCodeWrite = {};
    const next = {
      code: normalizeWsCode(form.code),
      label: form.label.trim(),
      billing_class: form.billing_class,
      sort_order: form.sort_order.trim() === '' ? null : Number(form.sort_order.trim()),
    };
    if (!code || next.code !== code.code) body.code = next.code;
    if (!code || next.label !== code.label) body.label = next.label;
    if (!code || next.billing_class !== code.billingClass) body.billing_class = next.billing_class;
    if (next.sort_order !== null && (!code || next.sort_order !== code.sortOrder)) body.sort_order = next.sort_order;
    if (code && form.active !== code.active) body.active = form.active;
    if (code && Object.keys(body).length === 0) return setError('Nothing has changed.');
    const done = {
      onSuccess: () => {
        toast.success(code ? `Code ${next.code} saved` : `Code ${next.code} added`);
        onClose();
      },
      onError: (err: unknown) => setError(wsRefusal(err, 'The code could not be saved.')),
    };
    if (code) update.mutate({ id: code.id, body }, done);
    else create.mutate(body, done);
  };

  return (
    <WsDialog
      open
      title={code ? `Edit code ${code.code}` : 'Add an activity code'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WS_PRIMARY} onClick={save} disabled={pending}>
            {pending ? 'Saving…' : code ? 'Save code' : 'Add code'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="ws-code-dialog">
        <WsFormError message={error} />
        {code?.starter && (
          <p className="text-xs text-fieldiq-muted">A starter code: change it to suit how your crews book time.</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <WsField label="Code" hint="Letters, digits, - and _. Saved in capitals.">
            <input className={`${WS_INPUT} font-mono`} maxLength={20} value={form.code} onChange={(e) => set('code', e.target.value)} aria-label="Code" />
          </WsField>
          <WsField label="Order" hint="Lower comes first. Blank: 0 on a new code.">
            <input className={WS_INPUT} inputMode="numeric" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} aria-label="Order" />
          </WsField>
        </div>
        <WsField label="Description">
          <input className={WS_INPUT} maxLength={200} value={form.label} onChange={(e) => set('label', e.target.value)} aria-label="Description" />
        </WsField>
        <WsField label="Bills as">
          <select className={WS_INPUT} value={form.billing_class} onChange={(e) => set('billing_class', e.target.value)} aria-label="Bills as">
            <option value="">Select…</option>
            {(catalog?.billingClasses ?? []).map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </WsField>
        {code && (
          <label className="flex items-center gap-2 text-sm text-fieldiq-text">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} aria-label="Active" />
            Active (a retired code is kept on old tickets but not offered for new time)
          </label>
        )}
      </div>
    </WsDialog>
  );
}

function StarterResult({ result, onClose }: { result: WsStarterResult; onClose: () => void }) {
  return (
    <WsDialog
      open
      title="Starter codes"
      onClose={onClose}
      footer={
        <button type="button" className={WS_PRIMARY} onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="space-y-2 text-sm text-fieldiq-text" data-testid="ws-starter-result">
        <p>
          {result.counts.inserted === 0
            ? 'Nothing new: you already have every starter code.'
            : `Added ${result.counts.inserted} starter code${result.counts.inserted === 1 ? '' : 's'}.`}
          {result.counts.skipped > 0 && ` Skipped ${result.counts.skipped} you already hold.`}
        </p>
        {result.skipped.some((s) => s.existingCode !== s.code) && (
          <ul className="list-disc pl-5 text-xs text-fieldiq-muted">
            {result.skipped
              .filter((s) => s.existingCode !== s.code)
              .map((s) => (
                <li key={s.code}>
                  {s.code} — you hold it as {s.existingCode}
                </li>
              ))}
          </ul>
        )}
      </div>
    </WsDialog>
  );
}

export default function WsActivityCodesScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('active');
  const active: WsCodeActive = raw === 'false' || raw === 'all' ? raw : 'true';
  const setActive = (v: WsCodeActive) => {
    const p = new URLSearchParams(searchParams);
    if (v === 'true') p.delete('active');
    else p.set('active', v);
    setSearchParams(p, { replace: true });
  };
  const catalog = useWsMeta().data?.data ?? null;
  const query = useWsActivityCodes(active);
  const allQuery = useWsActivityCodes('all');
  const starter = useAddWsStarterCodes();
  const canAdmin = canAdminWs(user?.role);
  const [dialog, setDialog] = useState<{ code: WsActivityCode | null } | null>(null);
  const [starterResult, setStarterResult] = useState<WsStarterResult | null>(null);

  const codes = query.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const listMeta = allQuery.data?.meta ?? query.data?.meta ?? null;
  const countsLoading = allQuery.isLoading;
  const countOf = (pick: (c: WsActivityCode) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const missing = listMeta?.starterMissing ?? null;

  const columns: WsColumn<WsActivityCode>[] = [
    { key: 'code', header: 'Code', render: (c) => <span className="font-mono text-sm font-extrabold text-white">{c.code}</span> },
    { key: 'label', header: 'Description', render: (c) => c.label || '—' },
    { key: 'billing', header: 'Bills as', render: (c) => <WsBadge skin={wsBillingSkin(c.billingClass, c.billingClassLabel)} value={c.billingClass} /> },
    { key: 'source', header: 'Source', render: (c) => (c.starter ? 'Starter' : 'Yours') },
    { key: 'status', header: 'Status', render: (c) => (c.active ? 'Active' : <WsBadge skin={WS_RETIRED_SKIN} value="retired" />) },
  ];

  return (
    <div data-testid="ws-codes-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="Activity codes"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdmin ? (
            <div className="flex flex-wrap gap-2">
              {missing !== null && missing > 0 && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--secondary"
                  disabled={starter.isPending}
                  onClick={() =>
                    starter.mutate(undefined, {
                      onSuccess: (res) => {
                        if (res.data) setStarterResult(res.data);
                      },
                      onError: (err) => toast.error(wsRefusal(err, 'The starter codes could not be added.')),
                    })
                  }
                >
                  <PackagePlus className="size-4" aria-hidden="true" />
                  {starter.isPending ? 'Adding…' : `Add starter codes (${missing})`}
                </button>
              )}
              <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setDialog({ code: null })}>
                <Plus className="size-4" aria-hidden="true" />
                New code
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <ListChecks className="size-4" aria-hidden="true" />, label: 'Codes', sub: 'Held', tone: 'neutral', loading: countsLoading, value: displayValue(listMeta?.total) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Active', tone: 'neutral', loading: countsLoading, value: countOf((c) => c.active) },
          { icon: <PackagePlus className="size-4" aria-hidden="true" />, label: 'Starter', sub: 'Not yet held', tone: 'accent', loading: countsLoading, value: displayValue(missing) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WsChipRow label="Filter by status">
            {FILTERS.map((f) => (
              <WsChip
                key={f.value}
                label={f.label}
                active={active === f.value}
                onClick={() => setActive(f.value)}
                count={countsLoading ? undefined : countOf((c) => (f.value === 'all' ? true : c.active === (f.value === 'true')))}
              />
            ))}
          </WsChipRow>

          <WsPanel
            eyebrow="Tour sheet"
            title="Activity codes"
            testId="ws-code-register"
            footer={!query.isLoading && !queryLoadFailure(query) && codes.length > 0 ? `${codes.length} code${codes.length === 1 ? '' : 's'}` : undefined}
          >
            <WsRegister
              query={query}
              rows={codes}
              columns={columns}
              rail={(c) => wsBillingSkin(c.billingClass).rail}
              onOpen={canAdmin ? (c) => setDialog({ code: c }) : undefined}
              subject="activity codes"
              testId="ws-code"
              minWidth="min-w-[720px]"
              empty={
                active === 'false' ? (
                  <WsEmpty>No code is retired.</WsEmpty>
                ) : (
                  <WsEmpty>There are no activity codes yet.{canAdmin ? ' Add the starter codes, or your own.' : ''}</WsEmpty>
                )
              }
              card={(c) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-mono text-sm font-extrabold text-white">{c.code}</p>
                    <WsBadge skin={c.active ? wsBillingSkin(c.billingClass, c.billingClassLabel) : WS_RETIRED_SKIN} value={c.active ? c.billingClass : 'retired'} />
                  </div>
                  <p className="mt-1 text-xs text-fieldiq-text">{c.label || '—'}</p>
                </>
              )}
            />
          </WsPanel>
        </div>
      </section>

      {dialog && <CodeDialog code={dialog.code} catalog={catalog} onClose={() => setDialog(null)} />}
      {starterResult && <StarterResult result={starterResult} onClose={() => setStarterResult(null)} />}
    </div>
  );
}
